<!--
  Ported from vendor-ref/mara/app/ui/src/components/nanome/ComponentVisualizations.vue.
  toggle-visualization / update-visualization REST calls became store.toggleVisualization, setHydrogenMode,
  setSurfaceOpacity. Preset rows, icons and matching come from @nanome-lite/state presets (VISUALIZATION_MENU).
-->
<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import Popover from 'primevue/popover';
import Button from 'primevue/button';
import ToggleSwitch from 'primevue/toggleswitch';
import SelectButton from 'primevue/selectbutton';
import Divider from 'primevue/divider';
import {
  HYDROGEN_MODE, SURFACE_OPACITY, VISUALIZATION_MENU, useLiteStore, visualizationName,
  type Component, type HydrogenRenderMode, type VisualizationModel,
} from '@nanome-lite/state';
import ComponentColoring from './ComponentColoring.vue';

const props = defineProps<{ component: Component | null }>();
const emit = defineEmits<{ hide: [] }>();

const store = useLiteStore();
const popover = ref<InstanceType<typeof Popover>>();
const panelColoring = ref<InstanceType<typeof ComponentColoring>>();
const ctxVisualizationKind = ref<VisualizationModel['kind'] | null>(null);
const ctxVisualizationEl = ref<EventTarget | null>(null);

/** Live view of the component from the store (the prop may be a snapshot). */
const live = computed(() => props.component ? store.componentBySerial(props.component.serial) ?? null : null);
const ctxVisualization = computed(() => live.value?.visualizations.find(v => v.kind === ctxVisualizationKind.value) ?? null);

const hasAtomistic = computed(() => live.value?.visualizations.some(v => v.kind === 'Atomistic' && v.visible) ?? false);

function getVisualization(name: string): VisualizationModel | undefined {
  return live.value?.visualizations.find(v => visualizationName(v) === name);
}
function getHydrogenMode(): HydrogenRenderMode {
  return live.value?.visualizations.find(v => v.kind === 'Atomistic')?.renderHydrogen ?? 'All';
}
function getSurfaceValue(): number | 'wireframe' {
  const vis = getVisualization('Surface');
  if (!vis) return SURFACE_OPACITY['Sheer'] ?? 0.15;
  if (vis.wireframe) return 'wireframe';
  return vis.opacity ?? 0.5;
}

const hydrogenOptions = Object.entries(HYDROGEN_MODE).map(([label, value]) => ({ label, value }));
const surfaceOptions = Object.entries(SURFACE_OPACITY).map(([label, value]) => ({ label, value }));
const HYDROGEN_ICONS: Record<string, string> = { None: 'i-tabler-circle-dashed', PolarOnly: 'i-tabler-timeline', All: 'i-tabler-circle-filled' };
const SURFACE_ICONS: Record<string, string> = { Wire: 'i-tabler-grid-4x4', Sheer: 'i-tabler-circle-dashed', Semi: 'i-tabler-circle-half-2', Opaque: 'i-tabler-circle-filled' };

function toggleVisualization(name: string) {
  const row = VISUALIZATION_MENU.find(m => m.name === name);
  if (!row || !live.value) return;
  store.toggleVisualization(live.value.serial, row.preset);
}
function setHydrogenMode(value: HydrogenRenderMode) { if (live.value) store.setHydrogenMode(live.value.serial, value); }
function setSurfaceOpacity(value: number | 'wireframe') { if (live.value) store.setSurfaceOpacity(live.value.serial, value); }

async function togglePanelColoring(e: Event, name: string) {
  panelColoring.value?.hide();
  if (ctxVisualizationEl.value === e.currentTarget) return;
  await nextTick();
  ctxVisualizationEl.value = e.currentTarget;
  ctxVisualizationKind.value = getVisualization(name)?.kind ?? null;
  panelColoring.value?.show(e);
}

defineExpose({
  show: (e: Event) => popover.value?.show(e),
  hide: () => popover.value?.hide(),
  toggle: (e: Event) => popover.value?.toggle(e),
  getVisualization,
  getVisualizationName: visualizationName,
});
</script>

<template>
  <Popover ref="popover" class="rd-6" pt:content="flex flex-col gap-3" @hide="emit('hide')">
    <template v-for="{ name, icon } of VISUALIZATION_MENU" :key="name">
      <div class="flex gap-2">
        <label class="grow-1 flex items-center gap-2 cursor-pointer">
          <i :class="icon" />
          <div class="mr-auto">
            {{ name }}
          </div>
          <ToggleSwitch
            :key="`${live?.serial}-${name}-${!!getVisualization(name)?.visible}`"
            :default-value="!!getVisualization(name)?.visible"
            class="ml-4"
            @value-change="toggleVisualization(name)"
          />
        </label>
        <Button
          :disabled="!getVisualization(name)?.visible"
          icon="i-tabler-palette"
          severity="secondary"
          size="small"
          rounded
          @click="togglePanelColoring($event, name)"
        />
      </div>
      <SelectButton
        v-if="name === 'Van der Waals'"
        :key="`h-${live?.serial}-${getHydrogenMode()}`"
        :dt="{ borderRadius: '1.5rem' }"
        :disabled="!hasAtomistic"
        :default-value="getHydrogenMode()"
        :options="hydrogenOptions"
        class="w-full"
        size="small"
        style="--togglebutton-content-border-radius: 1.25rem"
        option-label="label"
        option-value="value"
        @value-change="setHydrogenMode"
      >
        <template #option="{ option }">
          <i :class="HYDROGEN_ICONS[option.value]" />
          {{ option.label }}
        </template>
      </SelectButton>
      <SelectButton
        v-if="name === 'Surface'"
        :key="`s-${live?.serial}-${getSurfaceValue()}`"
        :dt="{ borderRadius: '1.5rem' }"
        :disabled="!getVisualization(name)?.visible"
        :default-value="getSurfaceValue()"
        :options="surfaceOptions"
        size="small"
        style="--togglebutton-content-border-radius: 1.25rem"
        option-label="label"
        option-value="value"
        @value-change="setSurfaceOpacity"
      >
        <template #option="{ option }">
          <i :class="SURFACE_ICONS[option.label]" />
          {{ option.label }}
        </template>
      </SelectButton>
      <Divider v-if="!['Ball and Stick', 'Stick', 'Wire', 'Surface'].includes(name)" class="my-0" />
    </template>
  </Popover>

  <ComponentColoring
    ref="panelColoring"
    :component="live"
    :visualization="ctxVisualization"
    @hide="ctxVisualizationEl = null"
  />
</template>
